import {isMatch} from 'lodash-es';

import {
    OrganizationStructureTabValidationErrors as ValidationErrorMessages,
    CommonInfoFieldNames,
    CompanyGroupFieldNames,
    ForeignParentsFieldNames,
    ParticipantsFieldNames,
} from 'pages/ais/organization-structure/organization-structure-page.constants';
import {TDeepPartial} from 'shared/types/generics';
import {areArraysLengthEqual, hasCommonKey} from 'shared/utils/arrays';
import {TOrganizationStructureDto} from 'store/slices/ais-slice/organization-structure';

interface IGetValidationMessagesArgs {
    invalidFields: string[];
    isAffiliationSet: boolean;
    isForeignParentSet: boolean;
}

export const getValidationMessages = ({
    invalidFields,
    isAffiliationSet,
    isForeignParentSet,
}: IGetValidationMessagesArgs): string[] => {
    const messages = [];

    if (hasCommonKey({source: invalidFields, search: Object.keys(CommonInfoFieldNames)})) {
        messages.push(ValidationErrorMessages.commonInfo);
    }
    if (isAffiliationSet) {
        if (hasCommonKey({source: invalidFields, search: Object.keys(CompanyGroupFieldNames)})) {
            messages.push(ValidationErrorMessages.companyGroup);
        }
        if (hasCommonKey({source: invalidFields, search: Object.keys(ParticipantsFieldNames)})) {
            messages.push(ValidationErrorMessages.participants);
        }
    }
    if (isForeignParentSet && hasCommonKey({source: invalidFields, search: Object.keys(ForeignParentsFieldNames)})) {
        messages.push(ValidationErrorMessages.foreignParentsInformation);
    }

    return messages;
};

const extractSubEntitiesFromOrgStructureDto = (orgStructure: TDeepPartial<TOrganizationStructureDto>) => {
    const {
        companyGroup,
        participants,
        ...restCommonInfo
    } = orgStructure ?? {};
    const {foreignParentsInformation: foreignParents, ...restCompanyGroup} = companyGroup ?? {};

    return ({
        commonInfo: {
            restCommonInfo,
        },
        companyGroup: {
            ...restCompanyGroup,
        },
        foreignParents,
        participants,
    });
};

interface IIsFormValuesDirtyArgs {
    initialValues: TOrganizationStructureDto;
    currentValues: TDeepPartial<TOrganizationStructureDto>;
}
export const isFormValuesDirty = ({initialValues, currentValues}: IIsFormValuesDirtyArgs): boolean => {
    const {
        commonInfo: initialCommonInfo,
        companyGroup: initialCompanyGroup,
        foreignParents: initialForeignParents,
        participants: initialParticipants,
    } = extractSubEntitiesFromOrgStructureDto(initialValues);
    const {
        commonInfo: currentCommonInfo,
        companyGroup: currentCompanyGroup,
        foreignParents: currentForeignParents,
        participants: currentParticipants,
    } = extractSubEntitiesFromOrgStructureDto(currentValues);

    if (
        !areArraysLengthEqual(initialForeignParents, currentForeignParents)
        || !areArraysLengthEqual(initialParticipants, currentParticipants)
    ) {
        return true;
    }

    return (
        !isMatch(initialCommonInfo, currentCommonInfo)
        || !isMatch(initialCompanyGroup, currentCompanyGroup)
        || !isMatch({a: initialForeignParents}, {a: currentForeignParents})
        || !isMatch({a: initialParticipants}, {a: currentParticipants})
    );
};
